import { Button, Col, Grid, Row, Spinner } from 'gu-ui-kit'
import i18n from 'i18next'
import i18next from 'i18next'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Form, reduxForm } from 'redux-form'
import { useHistory } from 'react-router'
import * as Yup from 'yup'
import validator from 'utils/asyncValidator'
import { formatToString } from 'utils/formatMoney'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useQueryParams from 'utils/hooks/useQueryParams'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import PaymentProfilesControl from 'modules/Forms/SharedControls/PaymentProfilesControl'
import {
  Account,
  AdTax,
  Amount,
  Credited,
  EmailDisapproved,
  ExchangeRate,
  PaymentOrderButton,
  Total,
  Wallets,
} from 'modules/Forms/Billing/subcomponents'
import { useBilling } from 'modules/Forms/Billing/useBilling'
import styles from 'modules/Forms/Billing/Billing-forms.module.scss'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'
import { notificationShow } from 'modules/Notifications/actions'
import { ROUTES } from 'constants/routes'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import BILLING_API from 'api/billing/v1'
import downloadFile from 'utils/downloadFile'
import { APP_PRODUCT } from 'constants/app-config'
import EmptyWalletNotification from 'modules/Forms/Billing/subcomponents/EmptyWalletNotification'
import { createPath } from 'history'
import qs from 'qs'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const validationSchema = Yup.object().shape({
  method: Yup.string().required('billing::errors.choose_payment_method'),
  payment_amount: Yup.object().shape({
    value: Yup.number()
      .typeError('billing::errors.required_field')
      .test({
        name: 'min_amount',
        test: function (value, ctx) {
          const { path, parent } = this
          const { min_amount = 0 } = ctx.from?.[1]?.value ?? {}

          if (!value) {
            return false
          }

          if (+value >= min_amount) {
            return true
          }

          return ctx.createError({
            path,
            message: i18n.t('billing::errors.specify_amount', {
              amount: formatToString(min_amount, parent.currency),
            }),
          })
        },
      })
      .required('billing::errors.required_field'),
    currency: Yup.string(),
  }),
  is_payment_profile: Yup.boolean(),
  payment_profile_id: Yup.string()
    .when('is_payment_profile', {
      is: true,
      then: (schema) => schema.required('billing::errors.required_field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  is_wallets: Yup.boolean(),
  wallet_id: Yup.string()
    .when('is_wallets', {
      is: true,
      then: (schema) => schema.required('billing::errors.required_field'),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  min_amount: Yup.number(),
  network_id: Yup.string().nullable(),
})

const CALCULATION_DELAY_TIME = 1000
const MAX_RETRIES = 5
const RETRY_DELAY = 5000

let BillingForm = ({
  handleSubmit,
  form,
  reset,
  autofill,
  accountId = null,
  onClose = () => {},
  networkId = null,
}) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation(['billing'])
  const dispatch = useDispatch()
  const {
    i18n,
    user: { is_payment_orders_creation_available = null, is_email_confirmed },
  } = useSelector(({ app }) => app)

  const { query, setQuery } = useQueryParams()
  const [formInitiated, setFormInitiated] = useState(false)
  const history = useHistory()
  const [redirectUrl, setRedirectUrl] = useState(null)
  const [summary, setSummary] = useState(null)
  const pathGen = useGeneratePathI18n()

  const [invoiceData, setInvoiceData] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  const handleRetry = () => {
    setRetryCount((prevCount) => prevCount + 1)
  }

  const fetchInvoiceDocumentData = async () => {
    try {
      const { response } = await BILLING_API.fetchInvoiceById({
        id: invoiceData.id,
      })

      if (response.data.document.status === 'success') {
        setRetryCount(() => 5)
        setInvoiceData({ ...invoiceData, documentSuccess: true })
        dispatch(
          notificationShow({
            type: 'success',
            message: i18next.t('billing::notifications.invoice.successfully_created', {
              number: invoiceData.number,
            }),
          }),
        )
      } else {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
        throw new Error('document not ready')
      }
    } catch (err) {
      handleRetry()
    }
  }

  useEffect(() => {
    if (retryCount > MAX_RETRIES) {
      dispatch(
        notificationShow({
          type: 'success',
          message: t('billing::notifications.invoice.slow_document_generation'),
          delay: 12000,
        }),
      )

      history.push(pathGen(ROUTES.FINANCE_INVOICES))
    }

    invoiceData && retryCount !== MAX_RETRIES && fetchInvoiceDocumentData()
  }, [retryCount, invoiceData]) // Trigger fetch when retryCount changes

  const {
    data: {
      activeMethod,
      accountData: { loaded: accountDataLoaded, loading: accountDataLoading },
      networkAccountData: { type },
      accountAmount,
    } = {},
    calculate,
    topUp,
    values = {},
  } = useBilling(form)

  const [billingConfig, setBillingConfig] = useLocalStorage(form, {})

  const {
    payment_amount: { value: amount = null, currency = null } = {},
    is_payment_profile = false,
    payment_profile_id = null,
    method = null,
    is_wallets = false,
    wallet_id = null,
    min_amount = 0,
  } = values

  useEffect(() => {
    const { value = null } = summary?.deposit?.payment ?? {}
    if (accountAmount && value && +amount !== value) {
      autofill('payment_amount.value', value)
    }
  }, [summary, accountAmount])

  let windowReference = null

  const isGetuniqMethod = useMemo(
    () =>
      !activeMethod
        ? false
        : ['user_balance', 'user_referral_balance', 'cashless'].includes(activeMethod.method),
    [activeMethod],
  )

  const callTouch = useCallback(() => {
    if (IS_APP_PRODUCT_GETUNIQ || !accountId) {
      return
    }
    typeof window?.ct === 'function' && window.ct('3mtpffra', 'goal', 'replenishment')
  }, [accountId, IS_APP_PRODUCT_GETUNIQ])

  const submit = useCallback(
    (values) => {
      if (!isGetuniqMethod) {
        windowReference = window.open('', '_blank')
      }

      topUp({
        ...values,
        accountId,
        networkType: type,
        formName: form,
      })
        .then(({ redirect_url, pay_url, invoice = null }) => {
          if (method === 'cashless' && invoice) {
            setInvoiceData(invoice)
            return
          }

          if (isGetuniqMethod && redirect_url?.length > 0) {
            callTouch()
            location.href = redirect_url
          } else if (pay_url?.length > 0) {
            if (windowReference) {
              windowReference.location = pay_url
            } else {
              location.href = pay_url
            }
          }

          if (!invoice && redirect_url?.length > 0) {
            setRedirectUrl(redirect_url)
          }
        })
        .catch(() => {
          if (windowReference) {
            windowReference.close()
          }
        })
        .finally(() => {
          const { method, payment_profile_id, wallet_id } = values
          setBillingConfig({ method, payment_profile_id, wallet_id })
        })
    },
    [
      accountId,
      type,
      setBillingConfig,
      isGetuniqMethod,
      setRedirectUrl,
      reset,
      form,
      windowReference,
    ],
  )

  useEffect(() => {
    global.getBack = () => {
      callTouch()
      reset()
      redirectUrl && history.push(redirectUrl)

      dispatch(
        notificationShow({
          type: 'success',
          message: t('billing::deposit_replenish.notifications.success'),
        }),
      )

      global.getBack = undefined
      setRedirectUrl(null)
    }
  }, [redirectUrl])

  const initForm = useCallback(() => {
    const { method = null, payment_profile_id = null, wallet_id = null } = billingConfig // сохраненные в localstorage данные

    const activeMethodName = activeMethod.method
    const isCurrentBillingConfig = method === activeMethodName
    const isCurrentWallet =
      isCurrentBillingConfig &&
      activeMethod?.params?.wallets?.findIndex(({ id }) => id === wallet_id) > -1
    const isCurrentPaymentProfile =
      isCurrentBillingConfig &&
      activeMethod?.payment_profiles?.findIndex(({ id }) => id === payment_profile_id) > -1

    const isPaymentProfile = !!activeMethod?.payment_profiles
    const paymentProfileId = isPaymentProfile ? activeMethod?.payment_profiles?.[0]?.id : null

    const isWallets = !!activeMethod?.params?.wallets
    const firstWalletId = activeMethod?.params?.wallets?.[0]?.id

    const walletId = isWallets ? (firstWalletId === null ? 'null' : firstWalletId) : null

    autofill('method', activeMethodName)
    autofill('is_payment_profile', isPaymentProfile)
    autofill('payment_profile_id', isCurrentPaymentProfile ? payment_profile_id : paymentProfileId)
    autofill('is_wallets', !!activeMethod?.params?.wallets)
    autofill('wallet_id', isCurrentWallet ? wallet_id : walletId)

    setFormInitiated(true)
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod) {
      setQuery({ ...query, method: activeMethod.method })
      initForm()
    } else {
      reset()
    }
  }, [activeMethod])

  useEffect(() => {
    if (activeMethod && accountDataLoaded) {
      initForm()
    }
  }, [activeMethod, accountDataLoaded])

  useEffect(() => {
    if (!currency || !activeMethod) {
      return
    }

    const { min_amounts_map = null, min_amounts = null } = activeMethod
    let min_amount = min_amounts?.[currency] ?? 0

    if (min_amounts_map?.length) {
      min_amount =
        min_amounts_map.find(
          (minAmount) =>
            ((payment_profile_id !== null &&
              minAmount?.payment_profile_id === payment_profile_id) ||
              (wallet_id !== null && minAmount?.wallet_id === wallet_id)) &&
            minAmount?.currency === currency,
        )?.value ?? 0
    }

    autofill('min_amount', min_amount)
  }, [currency, activeMethod, payment_profile_id, wallet_id])

  useEffect(() => autofill('payment_amount.value', amount), [min_amount])

  const isFormReady = useMemo(
    () =>
      amount &&
      method &&
      (!is_payment_profile || (is_payment_profile && payment_profile_id)) &&
      (!is_wallets || (is_wallets && wallet_id)) &&
      currency &&
      +amount >= +values.min_amount,

    [values],
  )

  const runCalculate = useCallback(() => {
    calculate({
      networkId,
      accountId,
      ...values,
      formName: form,
      delayTime: CALCULATION_DELAY_TIME,
    }).then(({ data }) => setSummary((prevValue) => ({ ...prevValue, ...data })))
  }, [accountId, values /*amount*/, form, CALCULATION_DELAY_TIME, networkId])

  const resetCalculatedData = useCallback(() => {
    setSummary(null)
  }, [])

  // список кошельков
  const wallets = useMemo(
    () => activeMethod?.params?.wallets ?? null,
    [formInitiated, activeMethod, accountDataLoaded],
  )

  // платёжные профили
  const paymentProfiles = useMemo(
    () => activeMethod?.payment_profiles ?? null,
    [formInitiated, activeMethod],
  )

  // кошелек
  const wallet = useMemo(() => {
    if (wallet_id) {
      if (activeMethod?.method === 'user_referral_balance') {
        return activeMethod?.params.wallets?.[0]
      }

      return wallets?.find(({ id }) => id === wallet_id) || false
    }

    return false
  }, [formInitiated, wallet_id, wallets, accountDataLoaded, activeMethod])

  // спец валюта от способа пополнения
  const paymentProfileCurrency = useMemo(
    () =>
      (payment_profile_id &&
        paymentProfiles?.find(({ id }) => id === payment_profile_id)?.currency) ||
      false,
    [payment_profile_id, paymentProfiles, formInitiated],
  )

  // список валют
  // если нет спец валют используем валюты из списка валют
  // если нет ни того ни другого вообще не используем список
  const currencies = useMemo(
    () => (wallet?.currency || paymentProfileCurrency ? null : activeMethod?.currencies),
    [formInitiated, activeMethod, paymentProfileCurrency, wallet?.currency],
  )

  const currentCurrency = useMemo(
    () => paymentProfileCurrency || wallet?.currency || currencies?.[0]?.currency || null,
    [currencies, paymentProfileCurrency, wallet?.currency, formInitiated],
  )

  useEffect(
    () => autofill('payment_amount.currency', currentCurrency),
    [currentCurrency, currencies, activeMethod],
  )

  useEffect(
    () => calculate.finished && !isFormReady && resetCalculatedData(),
    [isFormReady, calculate.finished],
  )

  const canCreatePaymentOrderLink = useMemo(
    () =>
      !['user_balance', 'user_referral_balance', 'cashless', 'lucky'].includes(
        activeMethod?.method,
      ),
    [activeMethod],
  )

  //more code duplication for god of duplication!
  const isAmountDisabled = useMemo(() => {
    switch (true) {
      case is_wallets && (!wallet || wallet?.balance === 0):
        return true
      case !is_wallets &&
        paymentProfiles !== null &&
        (paymentProfiles?.length === 0 || !payment_profile_id):
        return true
      default:
        return false
    }
  }, [is_wallets, wallet, paymentProfiles, payment_profile_id])

  useEffect(() => {
    if (!isFormReady) {
      return
    }
    resetCalculatedData()
    !isAmountDisabled && runCalculate()
  }, [isFormReady, values, isAmountDisabled])

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <EmailDisapproved className={'margin-bottom-24'} />

      <PaymentProfilesControl
        disabled={!is_email_confirmed}
        className={styles['billing__row']}
        readOnly={calculate.progress || !paymentProfiles?.length}
        i18n={i18n}
        paymentProfiles={paymentProfiles}
        t={t}
        addUrlParams={{ payment_method: activeMethod?.method }}
      />
      <Account
        className={styles['billing__row']}
        t={t}
        i18n={i18n}
        disabled={!is_email_confirmed}
        {...activeMethod}
      />

      <EmptyWalletNotification
        className={'margin-bottom-24'}
        activeMethod={activeMethod}
        wallet={wallet}
      />

      <Wallets
        walletsLoading={accountDataLoading}
        className={styles['billing__row']}
        disabled={calculate.progress || !is_email_confirmed}
        wallets={wallets}
        t={t}
      />

      <Amount
        name={'payment_amount.value'}
        currencyName={'payment_amount.currency'}
        className={styles['billing__row']}
        currencies={currencies}
        currency={currency}
        label={t('amount')}
        disabled={!is_email_confirmed || isAmountDisabled}
        t={t}
      />
      {calculate.finished && (
        <div className={styles['billing__deposit']}>
          <div className={styles['billing__amount-rounding']}>
            <Grid>
              {accountId && (
                <>
                  <Row
                    alignItems="center"
                    justifyContent="between"
                  >
                    <Col
                      s={{ span: 12, order: 1 }}
                      sm={{ span: 'auto', order: 1 }}
                    >
                      {/* Будет зачислено */}
                      <Credited {...summary} />
                      {/* Рекламный сбор */}
                      <AdTax {...summary} />
                    </Col>

                    <Col
                      s={{ span: 12, order: 2 }}
                      sm={{ span: 'auto', order: 2 }}
                    >
                      {/* Округлить до */}
                      {/*<RoundTo {...transfer} onClick={handleClickRound} />*/}
                    </Col>
                  </Row>
                </>
              )}
            </Grid>
          </div>
          <div className={styles['billing__calculated-data']}>
            <Grid>
              <Row>
                <Col
                  s={{ span: 12, order: 1 }}
                  sm={{ span: 12, order: 1 }}
                  lg={{ span: 12, order: 2 }}
                  className={styles['billing__total-with-commission']}
                >
                  {/* Итоговая сумма */}
                  <Total {...summary} />
                  <span className={styles['billing__total-without-commission']}>
                    <ExchangeRate {...summary} />
                  </span>
                </Col>
              </Row>
            </Grid>
          </div>

          {calculate.progress && (
            <div className={styles['billing__deposit-spinner']}>
              <Spinner color="Label-GU-Primary" />
            </div>
          )}
        </div>
      )}

      <Grid
        gap={16}
        direction={breakpoints.sm ? 'row' : 'column-reverse'}
        className={'margin-top-40'}
      >
        <Col
          s={{
            span: 12,
            order: 1,
          }}
          sm={{
            span: 6,
            order: 2,
          }}
        >
          {invoiceData?.documentSuccess ? (
            <Button
              className={styles['billing__button']}
              bgColor={'Label-GU-Primary'}
              type="submit"
              size="l"
              variant="primary"
              onClick={(e) => {
                e.preventDefault()
                downloadFile(
                  `/api/app/v1/billing/invoices/${invoiceData.id}/document`,
                  `${APP_PRODUCT}-'invoice-'${invoiceData.invoice_number}`,
                )
              }}
            >
              {t('billing::download_invoice')}
            </Button>
          ) : (
            <Button
              className={styles['billing__button']}
              bgColor={calculate.finished ? 'Label-GU-Primary' : 'none'}
              type="submit"
              size="l"
              disabled={
                (invoiceData && !invoiceData?.documentSuccess) ||
                !calculate.finished ||
                calculate.progress ||
                topUp.progress ||
                !summary
              }
              variant="primary"
            >
              {calculate.progress ||
              topUp.progress ||
              (invoiceData && !invoiceData?.documentSuccess)
                ? invoiceData && !invoiceData?.documentSuccess
                  ? t('notifications.invoice.document_processing')
                  : t('loading')
                : t(activeMethod?.method === 'cashless' ? 'issue_invoice' : 'pay')}
            </Button>
          )}
        </Col>
        <Col
          className={styles['billing__buttons-reset']}
          s={{
            span: 12,
            order: 3,
          }}
          sm={{
            span: 6,
            order: 1,
          }}
        >
          {invoiceData ? (
            <Button
              className={styles['billing__button']}
              size="l"
              component={Link}
              to={createPath({
                pathname: pathGen(ROUTES.FINANCE_INVOICES),
                search: qs.stringify(
                  {
                    payment_profile_ids: [payment_profile_id],
                  },
                  {
                    arrayFormat: 'brackets',
                  },
                ),
              })}
            >
              {t('billing::go_to_invoices')}
            </Button>
          ) : (
            <Button
              className={styles['billing__button']}
              size="l"
              onClick={onClose}
              variant={'ghost'}
              grey
            >
              {t('common::buttons.go_back')}
            </Button>
          )}
        </Col>

        {canCreatePaymentOrderLink &&
          is_payment_orders_creation_available &&
          !calculate.progress &&
          calculate.finished && (
            <Col
              s={{
                span: 12,
                order: 2,
              }}
              sm={{ span: 12, order: 3 }}
            >
              <Grid justifyContent={'center'}>
                <PaymentOrderButton form={form} />
              </Grid>
            </Col>
          )}
      </Grid>
    </Form>
  )
}

BillingForm.propTypes = {
  handleSubmit: PropTypes.func,
  form: PropTypes.string,
  reset: PropTypes.func,
  accountId: PropTypes.string,
  autofill: PropTypes.func,
  focus: PropTypes.func,
  asyncValidate: PropTypes.func,
  blur: PropTypes.func,
  onClose: PropTypes.func,
  dirty: PropTypes.bool,
  valid: PropTypes.bool,
  networkId: PropTypes.string,
}

const BillingFormRedux = reduxForm({
  form: 'billing',
  enableReinitialize: true,
  shouldAsyncValidate,
  touchOnBlur: false,
  touchOnChange: true,
  asyncValidate: validator(validationSchema),
})(BillingForm)

export default BillingFormRedux
